/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import './styles.scss'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { UserFeedback } from '@ta-interaktiv/react-user-feedback'
import { Waypoint } from 'react-waypoint'

const ARTICLE_ID = '21190860'
const ADDITIONAL_SHARES = 1658

export function AppComponent() {
  return (
    <div className='index'>
      <Masthead articleId={ARTICLE_ID} additionalShares={ADDITIONAL_SHARES} />
      <ThemeSwitch />

      <header className='ui vertical fitted segment'>
        <div className='ui container'>
          <h1 className='ui header' style={{ marginBottom: 0 }}>
            Das steckt drin
          </h1>
          <h2 className='ui header' style={{ marginTop: 0 }}>
            <div className='small sub header'>
              Aus was besteht eigentlich Nutella, Ketchup oder Rivella? Wir
              zeigen die Zutaten als grafische Schichtbilder so, wie Sie es noch
              nie gesehen haben.
            </div>
          </h2>
          <p className='ta author'>
            Von Patrick Vögeli, Marc Brupbacher, Reto Oeschger und Daniel
            Böniger
          </p>
        </div>
      </header>

      <main className='ui vertical fitted segment'>
        <div className='ui text container'>
          <p>
            Wir haben verschiedene Lebensmittelklassiker in ihre Zutaten zerlegt
            und diese optisch ins rechte Licht gerückt – damit sich jeder selber
            ein Bild davon machen kann, was drinsteckt.
          </p>
        </div>
      </main>

      <main className='ui vertical fitted segment'>
        <div className='ui container'>
          <div className='ui medium infographic dividing header' />
          <div className='title box'>
            <h2 className='ui medium infographic header'>Nutella (630 g)</h2>
          </div>
          <div className='title box two'>
            <h2 className='ui medium infographic header lighter'>enthält</h2>
          </div>
        </div>

        <div className='ui container wrapper'>
          <div className='image desktop mobile hidden'>
            <img
              src={require('./desktop-nutella.jpg')}
              alt=''
              className='ui centered fluid image'
            />
            <img
              src={require('./desktop-nutella-overlay.svg')}
              alt=''
              className='ui centered fluid image overlay'
            />
          </div>
          <div className='image mobile only'>
            <img
              src={require('./mobile-nutella.jpg')}
              alt=''
              className='ui centered fluid image'
            />
            <img
              src={require('./mobile-nutella-overlay.svg')}
              alt=''
              className='ui centered fluid image overlay'
            />
          </div>

          <div className='ui textbox'>
            <p className='text source full-width'>
              Weitere Zutaten:
              <br />
              Emulgator: Lecithine (Soja), Vanillin.
            </p>
          </div>
        </div>

        <div className='ui text container'>
          <p>
            Kinder lieben diese braune Creme auf ihrem Zmorgebrot, Eltern sehen
            den Brotaufstrich Nutella weniger gern. Weshalb? Problematisch ist
            vor allem, dass er zur Hälfte aus Zucker besteht. Nicht unbedingt
            ein Inhaltsstoff, der den Kleinen guttut: Der Zusammenhang zwischen
            dem stark gestiegenen Zuckerverzehr in unserer Gesellschaft und der
            bedrohlichen Zunahme von Übergewicht und Fettsucht bei Kindern und
            Jugendlichen ist evident.
          </p>
          <p>
            Die zweite Zutat von Nutella, die problematisch scheint, ist Palmöl.
            Es besteht mindestens zur Hälfte aus gesättigten Fettsäuren, die
            hohe Cholesterinwerte und Herzkrankheiten verursachen können.
            Daneben enthält es sogenannte MCPD-Fettsäureester, die gar als
            krebserregend gelten. Damit in Kontakt kommen Kinder sowieso – etwa
            im Guetsli nach dem Znacht. Aber schon morgens? Als Frühstück taugt
            ein Müesli wohl doch eher – bloss mögen das die Kleinen weniger
            gern.
          </p>
          <p>
            Zucker und Fette sind auf dem Etikett oft nur schwer zu erkennen.
            Die Visualisierung der Inhaltsstoffe macht es jedoch deutlich: Der
            Schokoladen-Brotaufstrich ist eine Zucker-Fett-Creme und enthält
            kaum Kakao.
          </p>
        </div>
      </main>

      <main className='ui vertical fitted segment'>
        <div className='ui container'>
          <div className='ui medium infographic dividing header' />
          <div className='title box'>
            <h2 className='ui medium infographic header'>
              Raketenglace (50 ml)
            </h2>
          </div>
          <div className='title box two'>
            <h2 className='ui medium infographic header lighter'>enthält</h2>
          </div>
        </div>

        <div className='ui container wrapper'>
          <div className='image desktop mobile hidden'>
            <img
              src={require('./desktop-rakete.jpg')}
              alt=''
              className='ui centered fluid image'
            />
            <img
              src={require('./desktop-rakete-overlay.svg')}
              alt=''
              className='ui centered fluid image overlay'
            />
          </div>
          <div className='image mobile only'>
            <img
              src={require('./mobile-rakete.jpg')}
              alt=''
              className='ui centered fluid image'
            />
            <img
              src={require('./mobile-rakete-overlay.svg')}
              alt=''
              className='ui centered fluid image overlay'
            />
          </div>

          <div className='ui textbox'>
            <p className='text source full-width'>
              Weitere Zutaten:
              <br />
              Gemüse- und Pflanzenkonzentrate; Stabilisator;
              <br />
              natürl. Aromen; Emulgator.
            </p>
          </div>
        </div>

        <div className='ui text container'>
          <p>
            Inspiriert von der ersten Mondlandung, ist die 1969 lancierte
            «Rakete» nichts weniger als ein Glace-Klassiker. Und seien wir nicht
            päpstlicher als der Papst: Ab und zu darf es doch etwas Süsses sein,
            gerade wenn die Kinder genau dieses Wasser-Stängelglace wählen, das
            übrigens nur im Sommer hergestellt wird. Schliesslich besteht die
            Rakete zu drei Vierteln aus Wasser.
          </p>
          <p>
            Auch der Zuckergehalt scheint okay, vergleicht man ihn mit «Magnum
            Classic» vom gleichen Hersteller: Im schokoladenüberzogenen
            Rahmglace ist nicht nur doppelt so viel Zucker drin wie in der
            Rakete, sondern es kommt obendrauf noch rund ein Fünftel Fett,
            welches beim Wasserglace fehlt. Übrigens kostete die Rakete vor rund
            50 Jahren noch 30 Rappen am Kiosk – heute muss man bereits 1.30
            Franken dafür hinlegen.
          </p>
        </div>
      </main>

      <main className='ui vertical fitted segment'>
        <div className='ui container'>
          <div className='ui medium infographic dividing header' />
          <div className='title box'>
            <h2 className='ui medium infographic header'>Ketchup (342 g)</h2>
          </div>
          <div className='title box two'>
            <h2 className='ui medium infographic header lighter'>enthält</h2>
          </div>
        </div>

        <div className='ui container wrapper'>
          <div className='image desktop mobile hidden'>
            <img
              src={require('./desktop-ketchup.jpg')}
              alt=''
              className='ui centered fluid image'
            />
            <img
              src={require('./desktop-ketchup-overlay.svg')}
              alt=''
              className='ui centered fluid image overlay'
            />
          </div>
          <div className='image mobile only'>
            <img
              src={require('./mobile-ketchup.jpg')}
              alt=''
              className='ui centered fluid image'
            />
            <img
              src={require('./mobile-ketchup-overlay.svg')}
              alt=''
              className='ui centered fluid image overlay'
            />
          </div>

          <div className='ui textbox'>
            <p className='text source full-width'>
              Weitere Zutaten:
              <br />
              Salz, Gewürz- und Kräuterextrakte.
            </p>
          </div>
        </div>

        <div className='ui text container'>
          <p>
            Was wären Pommes frites ohne die «rote Sauce»? Und wer schon
            verglichen hat, dürfte gleich auch zugeben: Das Tomato Ketchup von
            Heinz schmeckt einfach sehr gut, wie auch verschiedene
            Konsumententests immer wieder zeigen. Es besteht im Wesentlichen aus
            drei Hauptzutaten: Je zu einem Viertel aus Zucker und Essig – woraus
            das typische Süss-Säure-Spiel der Würzsauce entsteht.
          </p>
          <p>
            Die andere Hälfte ist Tomatenmark, sprich eingekochte Tomaten. Wie
            hoch deren prozentualer Anteil ist, gibt der Hersteller nicht an.
            Was wir aber wissen: In 342 Gramm Ketchup stecken 506 Gramm Tomaten
            drin – das sind circa sechs mittelgrosse Tomaten. Mit dieser
            gesicherten Angabe haben wir selber den ungefähren Tomatenmarkgehalt
            berechnet.
          </p>
        </div>
      </main>

      {/* Waypoint */}
      <Waypoint
        scrollableAncestor={window}
        onEnter={event => {
          if (event.previousPosition === 'below') {
            window.dataLayer = window.dataLayer || []
            window.dataLayer.push({
              event: '50ContentRead'
            })
          }
        }}
      />

      <main className='ui vertical fitted segment'>
        <div className='ui container'>
          <div className='ui medium infographic dividing header' />
          <div className='title box'>
            <h2 className='ui medium infographic header'>Rivella (500 ml)</h2>
          </div>
          <div className='title box two'>
            <h2 className='ui medium infographic header lighter'>enthält</h2>
          </div>
        </div>

        <div className='ui container wrapper'>
          <div className='image desktop mobile hidden'>
            <img
              src={require('./desktop-rivella.jpg')}
              alt=''
              className='ui centered fluid image'
            />
            <img
              src={require('./desktop-rivella-overlay.svg')}
              alt=''
              className='ui centered fluid image overlay'
            />
          </div>
          <div className='image mobile only'>
            <img
              src={require('./mobile-rivella.jpg')}
              alt=''
              className='ui centered fluid image'
            />
            <img
              src={require('./mobile-rivella-overlay.svg')}
              alt=''
              className='ui centered fluid image overlay'
            />
          </div>

          <div className='ui textbox'>
            <p className='text source full-width'>
              Weitere Zutaten:
              <br />
              Kohlensäure, Milchsäure, natürliche Aromen.
            </p>
          </div>
        </div>

        <div className='ui text container'>
          <p>
            «Sportler trinken Rivella!» Dieser Slogan ist in den Köpfen der
            Schweizerinnen und Schweizer fix verankert – und, in der Tat, viele
            von uns glauben wirklich, dass dieses Getränk per se gesund ist.
            Leider ist der Wunsch hier der Vater des Gedankens: Rivella rot
            enthält fast annähernd so viel Zucker wie das verpönte «Coca-Cola»
            (15 gegenüber 17,5 Würfelzucker). Da hilft auch das gesunde
            Milchserum wenig, das ebenso in jeder Flasche steckt. Und weswegen
            man das 1950 erfundene Getränk ursprünglich als Molkebier
            bezeichnete.
          </p>
          <p>
            Die Weltgesundheitsorganisation WHO empfiehlt, nicht mehr als fünf
            Prozent der täglichen Energiezufuhr mit sogenanntem freien Zucker zu
            decken. Bei einem Erwachsenen bedeutet das pro Tag rund 25 Gramm,
            also nur etwa acht Stück Würfelzucker. Allein nach einem Glas
            Rivella Rot (250 Mililiter) ist dieser Grenzwert praktisch erreicht.
            Mit der kürzlich lancierten Sorte «Refresher» bietet der Hersteller
            neu einen leichten, weniger süssen Durstlöscher an.
          </p>
        </div>
      </main>

      <main className='ui vertical fitted segment'>
        <div className='ui container'>
          <div className='ui medium infographic dividing header' />
          <div className='title box'>
            <h2 className='ui medium infographic header'>
              Paprika Chips (90 g)
            </h2>
          </div>
          <div className='title box two'>
            <h2 className='ui medium infographic header lighter'>enthalten</h2>
          </div>
        </div>

        <div className='ui container wrapper'>
          <div className='image desktop mobile hidden'>
            <img
              src={require('./desktop-chips.jpg')}
              alt=''
              className='ui centered fluid image'
            />
            <img
              src={require('./desktop-chips-overlay.svg')}
              alt=''
              className='ui centered fluid image overlay'
            />
          </div>

          <div className='image mobile only'>
            <img
              src={require('./mobile-chips.jpg')}
              alt=''
              className='ui centered fluid image'
            />
            <img
              src={require('./mobile-chips-overlay.svg')}
              alt=''
              className='ui centered fluid image overlay'
            />
          </div>

          <div className='ui textbox'>
            <p className='text source full-width'>
              Weitere Zutaten:
              <br />
              Hefeextrakt, Kartoffelstärke, Farbstoff, Raucharoma,
              <br />
              Gewürzextrakt, natürliches Aroma.
            </p>
          </div>
        </div>

        <div className='ui text container'>
          <p>
            Die bekannten Chips von Zweifel kennen zwei Hauptsaisons: im Sommer
            die Grillsaison, aber auch die Weihnachtszeit, wenn Herr und Frau
            Schweizer das Caquelon aus dem Schrank holen und Fondue chinoise
            essen … Wie gesund sind eigentlich Chips, die man meist erst dann
            weglegt, wenn die Tüte leer ist? Nun gut, gegen die verwendeten
            Kartoffeln kann man nicht allzu viel haben, viel mehr stört viele
            die Portion Fett, die im Rapsöl steckt – immerhin ein Drittel des
            Gewichts! Die Gewürze wie Paprika, Salz und Knoblauch machen einen
            weiteren Zehntel aus – damit die Würzmischung sich gleichmässig auf
            den dünnen Kartoffelscheiben verteilt, lädt eine Maschine diese
            zuerst elektrostatisch auf, bevor mit einem Gebläse die Chips und
            die Würze vermengt werden.
          </p>
          <p>
            Kein Problem sind übrigens die vor einigen Jahren in den Fokus
            geratenen Acrylamide. Zweifel frittiert bei Temperaturen, die den
            Anteil dieses ungesunden Stoffes vergleichsweise gering bleiben
            lassen. Zudem werden zu dunkle Chips maschinell aussortiert.
          </p>
        </div>
      </main>

      <main className='ui vertical fitted attached segment' id='methodik'>
        <div className='ui text container' style={{ position: 'relative' }}>
          <h3 className='ui small infographic header'>Methodik</h3>

          <p>
            Die Schichtbilder stellen die ungefähre Zusammensetzung der Zutaten
            des Produkts dar. Für die Anteilsberechnung der Inhalte dienten
            Herstellerangaben sowie entsprechende Nährwerttabellen und in
            letzter Konsequenz Schätzungen. Dabei ist der Gewichtsanteil der
            Zutat zum Zeitpunkt der Verwendung bei der Produktion
            ausschlaggebend. Um ein möglichst echtes Gesamtverhältnis der
            Schichten grafisch darzustellen, wurden für flüssige Lebensmittel
            der Volumenanteil und für feste Produkte der Flächenanteil in
            Prozent ermittelt.
          </p>
          <p>
            Zu sehen sind also Datenvisualisierungen der prozentualen
            Gewichtsanteile und nicht die realen Mengen der einzelnen Zutaten,
            da diese wegen Dichte, Struktur und Luftbeimischung in unverrührtem
            Zustand mal mehr, mal weniger Fläche einnehmen würden. Beim Beispiel
            Nutella widerspiegelt der Zucker in der Visualisierung 52 Prozent
            des Volumens des Glases, da der Gewichtsanteil der Zutat auch 52
            Prozent ausmacht. Würde man den dargestellten Zucker wiegen, würde
            er leichter sein als die angegebenen 330 Gramm, da noch viel Luft
            zwischen den Zuckerkristallen steckt.
          </p>
          <p>
            Zur Überprüfung wurden weitere Daten und Informationen von
            Konsumentenportalen wie dem K-Tipp beigezogen. Bei den abgebildeten
            Zuckerwürfeln handelt es sich um Würfel mit einem Gewicht von 3
            Gramm. Zutaten mit einem Anteil von weniger als 2 Prozent wurden
            nicht visualisiert und als weitere Zutaten unterhalb der Grafik
            aufgeführt.
          </p>
          <p>
            Die Inspiration der Schichtbilder stammt von der Verbraucherzentrale
            Hamburg, von der wir die freundliche Erlaubnis für die
            Weiterentwicklung des Konzepts erhielten.
          </p>
        </div>
      </main>

      <Footer
        articleId={ARTICLE_ID}
        additionalShares={ADDITIONAL_SHARES}
        topHalfClassName='grey'
        bottomHalfClassName='white'
        hidePortfolioTeaser
        questions={[
          'Wie informativ war der Beitrag?',
          'Wie unterhaltsam war er?',
          'Wie gefällt Ihnen die Gestaltung?'
        ]}
        credits={[
          [
            'Konzept, Programmierung und Grafiken',
            'Patrick Vögeli und Marc Brupbacher, Interaktiv-Team'
          ],
          ['Text', 'Daniel Böniger'],
          ['Fotografie', 'Reto Oeschger und Samuel Schalch'],
          [
            'Inspiration',
            '<a href="https://shop.vzhh.de/ernaehrung/492015/milch-schnitte-das-steckt-drin.aspx" target="_blank">Verbraucherzentrale Hamburg</a>'
          ],
          [
            'Quellen',
            'Herstellerangaben, Nährwerttabelle, K-Tipp, eigene Berechnungen'
          ]
        ]}
      />
    </div>
  )
}
